/* ==========================================================================
   Featured reviews under Add to Bag

   The proof block under the button carried one fixed quote, the same on every
   product. This fills it with THAT product's own reviews and turns them over
   every few seconds.

   Where the reviews come from: the Klaviyo widget already on the page. Reading
   the rendered widget rather than calling an API keeps this to one source of
   truth — whatever the widget shows, this shows — and needs no keys. The widget
   renders late and renames its classes between versions, so the selectors are a
   list of candidates plus a setting, and if none of them match, the block is
   left exactly as the theme rendered it. A silent no-op beats an empty quote.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = window.gioFeaturedReviews || {};
  var HOST_SEL = '.cta-proof';
  var INTERVAL = Math.max(1500, (Number(CFG.interval) || 3) * 1000);
  var MAX = Math.max(2, Number(CFG.max) || 5);

  // Candidates, widest last. The first that returns two or more usable reviews
  // wins; a setting can name a selector of its own when the widget changes again.
  var CARD_SELECTORS = [
    CFG.source,
    '.kl_reviews__review_item',
    '[class*="kl_reviews__review_item"]',
    '.kl_reviews__review',
    '[class*="review-item"]'
  ].filter(Boolean);

  var QUOTE_PARTS = ['.kl_reviews__review__content', '[class*="review__content"]', 'blockquote'];
  var AUTHOR_SEL = '.kl_reviews__review__author, [class*="review__author"], [class*="author"]';
  // The author line carries "Verified buyer" as a nested badge; the name is
  // what belongs under a quote.
  var AUTHOR_STRIP_SEL = '.kl_reviews__review__verified, [class*="verified"]';

  function host() {
    return document.querySelector(HOST_SEL);
  }

  function clean(node) {
    return node ? String(node.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  function harvest() {
    for (var i = 0; i < CARD_SELECTORS.length; i++) {
      var cards = Array.prototype.slice.call(document.querySelectorAll(CARD_SELECTORS[i]));
      var out = [];

      cards.forEach(function (card) {
        if (out.length >= MAX) return;
        // The summary header matches several of these selectors; a review has a
        // sentence in it, not a word.
        /* The review's own text, and nothing else. This used to fall back to
           the whole card when the text was short or not rendered yet, which
           put the date, the title and the name inside the quote marks
           ("24 days agoGreat flavorMaria V.Verified buyer"). A card without a
           proper sentence is skipped instead. Each selector is tried in
           turn: a selector LIST returns whichever match comes first in the
           document, which on this widget can be the wrong element. */
        var quote = '';
        for (var q = 0; q < QUOTE_PARTS.length && !quote; q++) {
          quote = clean(card.querySelector(QUOTE_PARTS[q]));
        }
        if (quote.length < 25) return;
        var authorEl = card.querySelector(AUTHOR_SEL);
        var verified = !!card.querySelector(AUTHOR_STRIP_SEL);
        var author = '';
        if (authorEl) {
          var copy = authorEl.cloneNode(true);
          Array.prototype.forEach.call(copy.querySelectorAll(AUTHOR_STRIP_SEL), function (bit) {
            bit.remove();
          });
          author = clean(copy);
        }
        out.push({ quote: quote, author: author, verified: verified });
      });

      if (out.length >= 2) return out;
    }
    return [];
  }

  function slots(el) {
    // The block is authored in the theme editor, so its inner markup is not
    // guaranteed. Take the named parts when they exist, the obvious ones if not.
    var quote = el.querySelector('.cta-proof-quote') || el.querySelector('p');
    // .cta-proof-who is the name line these templates actually use; without it
    // the quote turned over and the name under it never did, so every review
    // was credited to whoever the block was authored with.
    var author =
      el.querySelector('.cta-proof-who') ||
      el.querySelector('.cta-proof-author') ||
      el.querySelector('small') ||
      el.querySelector('p + span');
    return { quote: quote, author: author };
  }

  function start(el, reviews) {
    var parts = slots(el);
    if (!parts.quote) return;

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var index = 0;
    var timer = null;
    var paused = false;

    el.style.setProperty('--gio-review-fade', reduced ? '0ms' : '320ms');
    [parts.quote, parts.author].forEach(function (node) {
      if (node) node.style.transition = 'opacity var(--gio-review-fade, 320ms) ease';
    });

    function paint(step) {
      var review = reviews[index % reviews.length];
      parts.quote.textContent = '“' + review.quote + '”';
      if (parts.author) {
        parts.author.textContent = review.author
          ? review.author + (review.verified ? ' \u00b7 Verified buyer' : '')
          : '';
      }
      if (step) index += 1;
    }

    function turn() {
      if (paused) return;
      index += 1;
      if (reduced) {
        paint(false);
        return;
      }
      [parts.quote, parts.author].forEach(function (node) {
        if (node) node.style.opacity = '0';
      });
      setTimeout(function () {
        paint(false);
        [parts.quote, parts.author].forEach(function (node) {
          if (node) node.style.opacity = '1';
        });
      }, 320);
    }

    paint(false);

    function run() {
      clearInterval(timer);
      timer = setInterval(turn, INTERVAL);
    }

    // Nobody needs a carousel turning in a tab they are not looking at, and a
    // quote should hold still while it is being read.
    el.addEventListener('mouseenter', function () { paused = true; });
    el.addEventListener('mouseleave', function () { paused = false; });
    el.addEventListener('focusin', function () { paused = true; });
    el.addEventListener('focusout', function () { paused = false; });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) clearInterval(timer);
      else run();
    });

    el.setAttribute('aria-live', 'off');
    run();
  }

  function boot() {
    var el = host();
    if (!el || el.hasAttribute('data-gio-reviews-on')) return true;

    var reviews = harvest();
    if (reviews.length < 2) return false;

    el.setAttribute('data-gio-reviews-on', '');
    start(el, reviews);
    return true;
  }

  /* The widget does not render its reviews until the list is scrolled near, so a
     bounded poll can expire before a single card exists. Watch the document
     instead, and stop watching the moment we latch. The poll stays as a belt for
     browsers without MutationObserver. */
  if (!boot()) {
    var settled = false;

    var stop = function () {
      settled = true;
      if (observer) observer.disconnect();
      clearInterval(poll);
    };

    /* The observer watches the whole document, and these product pages mutate
       constantly — several apps redraw on load, on variant change and on
       scroll. Re-running the scan on every mutation put a querySelectorAll
       sweep in front of each one and the page stopped responding, so the work
       is coalesced onto a frame and the watch gives up after half a minute
       rather than running for the life of the page. */
    var queued = false;
    var attempt = function () {
      if (settled || queued) return;
      queued = true;
      // A timeout, not a frame: requestAnimationFrame does not run in a
      // background tab, which would leave `queued` raised for good.
      setTimeout(function () {
        queued = false;
        if (!settled && boot()) stop();
      }, 0);
    };

    var observer = null;
    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(attempt);
      observer.observe(document.body, { childList: true, subtree: true });
    }

    var tries = 0;
    var poll = setInterval(function () {
      if (settled) return;
      if (boot() || ++tries > 60) stop();
    }, 500);

    // A hard stop, so nothing is left watching a page that will never have
    // reviews on it.
    setTimeout(function () {
      if (!settled) stop();
    }, 30000);
  }
})();
